import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, X, FileText, Plus, CheckCircle, ChevronDown, Edit2, Trash2 } from 'lucide-react';
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import Footer from "../components/partials/Footer";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';
import countries from '../utils/countries';
import 'bootstrap/dist/css/bootstrap.min.css';
import LoadingSpinner from '../utils/LoadingSpinner';

const IdentificationPage = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [selfieImages, setSelfieImages] = useState({
        front: null,
        left: null,
        right: null
    });
    const [idDocuments, setIdDocuments] = useState([{
        id: null,
        idType: 'PASSPORT',
        documentName: '',
        documentImage: null,
        deliveryDateString: '',
        expiryDateString: '',
        /*deliveryAuthority: '',*/
        countryOfIssue: '',
    }]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [suggestedCountries, setSuggestedCountries] = useState([]);
    const [showDocumentTypeDropdown, setShowDocumentTypeDropdown] = useState({});
    const [showCountrySuggestions, setShowCountrySuggestions] = useState({});
    const [isUserVerified, setIsUserVerified] = useState(false);
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [hasData, setHasData] = useState(false);

    const loadIdentificationData = async () => {
        try {
            const user = await AuthService.getCurrentUser();
            if (user && user.username) {
                setCurrentUser(user);
                try {
                    const existingIdentification = await UserService.findIdentificationByUsername(
                        user.username,
                        'fr'
                    );
                    if (existingIdentification) {
                        setSelfieImages({
                            front: existingIdentification.selfieImageFront,
                            left: existingIdentification.selfieImageLeft,
                            right: existingIdentification.selfieImageRight
                        });
                        if (existingIdentification.idDocuments && existingIdentification.idDocuments.length > 0) {
                            setIdDocuments(existingIdentification.idDocuments.map(doc => ({
                                id: doc.id,
                                idType: doc.idType || 'PASSPORT',
                                documentName: doc.documentName || '',
                                documentImage: doc.documentImage,
                                deliveryDateString: doc.deliveryDate
                                    ? new Date(doc.deliveryDate).toISOString().split('T')[0]
                                    : '',
                                expiryDateString: doc.expiryDate
                                    ? new Date(doc.expiryDate).toISOString().split('T')[0]
                                    : '',
                                /*deliveryAuthority: doc.deliveryAuthority || '',*/
                                countryOfIssue: doc.countryOfIssue || '',
                            })));
                            setHasData(true);
                        }
                        const isVerified = await UserService.checkIdentification(user.username, 'fr');
                        setIsUserVerified(isVerified);
                    }
                } catch (error) {
                    if (error.response && error.response.status !== 404) {
                        console.error('Error fetching identification:', error);
                        toast.error('Erreur lors de la récupération des données d\'identification');
                    }
                }
            }
        } catch (error) {
            console.error('Error loading identification data:', error);
        }
    };

    useEffect(() => {
        const initializeScreen = async () => {
            try {
                await loadIdentificationData();
            } catch (error) {
                console.error('Error initializing screen:', error);
                toast.error('Une erreur est survenue lors de l\'initialisation');
            } finally {
                setInitialLoading(false);
            }
        };
        initializeScreen();
    }, []);

    const handleEditPress = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        loadIdentificationData();
    };

    const handleImagePickResult = (result, type, index) => {
        if (!isEditing && hasData) return;
        if (!result.canceled) {
            if (index !== null) {
                const newDocuments = [...idDocuments];
                newDocuments[index].documentImage = result.assets[0].uri;
                setIdDocuments(newDocuments);
            } else {
                setSelfieImages({ ...selfieImages, [type]: result.assets[0].uri });
            }
        }
    };

    const onDropSelfie = useCallback((acceptedFiles, type) => {
        if (!isEditing && hasData) return;
        const file = acceptedFiles[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setSelfieImages(prev => ({ ...prev, [type]: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    }, [isEditing, hasData]);

    const onDropDocument = useCallback((acceptedFiles, index) => {
        if (!isEditing && hasData) return;
        const file = acceptedFiles[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setIdDocuments(prev =>
                    prev.map((doc, i) =>
                        i === index ? { ...doc, documentImage: reader.result } : doc
                    )
                );
            };
            reader.readAsDataURL(file);
        }
    }, [isEditing, hasData]);

    const removeImage = (e, type, index, isDocument = false) => {
        if (!isEditing && hasData) return;
        e.stopPropagation();
        e.preventDefault();
        if (isDocument) {
            setIdDocuments(prev =>
                prev.map((doc, i) =>
                    i === index ? { ...doc, documentImage: null } : doc
                )
            );
        } else {
            setSelfieImages(prev => ({ ...prev, [type]: null }));
        }
    };

    const handleAddDocument = () => {
        if (!isEditing && hasData) return;
        setIdDocuments(prev => [
            ...prev,
            {
                id: null,
                idType: 'PASSPORT',
                documentName: '',
                documentImage: null,
                deliveryDateString: '',
                expiryDateString: '',
                /*deliveryAuthority: '',*/
                countryOfIssue: ''
            }
        ]);
    };

    const handleRemoveDocument = (index) => {
        if (!isEditing && hasData) return;
        const newDocuments = [...idDocuments];
        newDocuments.splice(index, 1);
        setIdDocuments(newDocuments);
    };

    const handleDateChange = (e, index, dateType) => {
        if (!isEditing && hasData) return;
        const selectedDate = e.target.value;
        const newDocuments = [...idDocuments];
        const dateString = selectedDate;
        if (dateType === 'delivery') {
            newDocuments[index].deliveryDateString = dateString;
            if (newDocuments[index].expiryDateString) {
                const deliveryDate = new Date(dateString);
                const expiryDate = new Date(newDocuments[index].expiryDateString);
                if (expiryDate <= deliveryDate) {
                    setErrors(prev => ({
                        ...prev,
                        [`expiryDate-${index}`]: 'La date d\'expiration doit être postérieure à la date de délivrance'
                    }));
                } else {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`expiryDate-${index}`];
                        return newErrors;
                    });
                }
            }
        } else {
            newDocuments[index].expiryDateString = dateString;
            if (newDocuments[index].deliveryDateString) {
                const deliveryDate = new Date(newDocuments[index].deliveryDateString);
                const expiryDate = new Date(dateString);
                if (expiryDate <= deliveryDate) {
                    setErrors(prev => ({
                        ...prev,
                        [`expiryDate-${index}`]: 'La date d\'expiration doit être postérieure à la date de délivrance'
                    }));
                } else {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`expiryDate-${index}`];
                        return newErrors;
                    });
                }
            }
        }
        setIdDocuments(newDocuments);
    };

    const handleCountryInputChange = (text, index) => {
        if (!isEditing && hasData) return;
        const newDocuments = [...idDocuments];
        newDocuments[index].countryOfIssue = text;
        setIdDocuments(newDocuments);
        if (text.length > 0) {
            const filteredCountries = countries.filter(country =>
                country.toLowerCase().includes(text.toLowerCase())
            );
            setSuggestedCountries(filteredCountries);
            setShowCountrySuggestions(prev => ({ ...prev, [index]: true }));
            const isValidCountry = countries.some(country =>
                country.toLowerCase() === text.toLowerCase()
            );
            if (!isValidCountry) {
                setErrors(prev => ({
                    ...prev,
                    [`country-${index}`]: 'Veuillez sélectionner un pays valide dans la liste'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`country-${index}`];
                    return newErrors;
                });
            }
        } else {
            setSuggestedCountries([]);
            setShowCountrySuggestions(prev => ({ ...prev, [index]: false }));
            setErrors(prev => ({
                ...prev,
                [`country-${index}`]: 'Ce champ est obligatoire'
            }));
        }
    };

    const handleCountrySelect = (country, index) => {
        const newDocuments = [...idDocuments];
        newDocuments[index].countryOfIssue = country;
        setIdDocuments(newDocuments);
        setShowCountrySuggestions(prev => ({ ...prev, [index]: false }));
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`country-${index}`];
            return newErrors;
        });
    };

    const handleDocumentTypePress = (index) => {
        setShowDocumentTypeDropdown(prev => ({
            ...Object.fromEntries(
                Object.keys(prev).map(key => [key, false])
            ),
            [index]: !prev[index]
        }));
    };


    const getDocumentTypeName = (type) => {
        switch (type) {
            case 'PASSPORT': return 'Passeport';
            case 'ID_CARD': return 'Carte d\'identité';
            case 'DRIVERS_LICENSE': return 'Permis de conduire';
            case 'PERMANENT_RESIDENT_CARD': return 'Carte de résident permanent';
            default: return type;
        }
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = {};
        if (!selfieImages.front) {
            newErrors.selfieFront = 'Un selfie de face est requis';
            isValid = false;
        }
        idDocuments.forEach((doc, index) => {
            if (!doc.documentName) {
                doc.documentName = getDocumentTypeName(doc.idType);
            }
            if (!doc.documentImage) {
                newErrors[`documentImage-${index}`] = 'Une image du document est requise';
                isValid = false;
            }
            if (!doc.deliveryDateString) {
                newErrors[`deliveryDate-${index}`] = 'La date de délivrance est requise';
                isValid = false;
            }
            if (!doc.expiryDateString) {
                newErrors[`expiryDate-${index}`] = 'La date d\'expiration est requise';
                isValid = false;
            } else if (doc.deliveryDateString) {
                const deliveryDate = new Date(doc.deliveryDateString);
                const expiryDate = new Date(doc.expiryDateString);
                if (expiryDate <= deliveryDate) {
                    newErrors[`expiryDate-${index}`] = 'La date d\'expiration doit être postérieure à la date de délivrance';
                    isValid = false;
                }
            }

            if (!doc.countryOfIssue) {
                newErrors[`country-${index}`] = 'Le pays de délivrance est requis';
                isValid = false;
            } else if (!countries.some(country => country.toLowerCase() === doc.countryOfIssue.toLowerCase())) {
                newErrors[`country-${index}`] = 'Veuillez sélectionner un pays valide dans la liste';
                isValid = false;
            }
        });
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs dans le formulaire.');
            return;
        }
        try {
            setIsLoading(true);
            if (!currentUser || !currentUser.username) {
                throw new Error('Utilisateur non connecté');
            }
            const requestData = {
                selfieImageFront: selfieImages.front,
                selfieImageLeft: selfieImages.left,
                selfieImageRight: selfieImages.right,
                username: currentUser.username,
                idDocuments: idDocuments.map(doc => ({
                    id: doc.id,
                    idType: doc.idType,
                    documentName: doc.documentName,
                    documentImage: doc.documentImage,
                    deliveryDateString: doc.deliveryDateString,
                    expiryDateString: doc.expiryDateString,
                    /*deliveryAuthority: doc.deliveryAuthority,*/
                    countryOfIssue: doc.countryOfIssue
                }))
            };
            await UserService.processIdentification(requestData);
            toast.success('Identification soumise avec succès!');
            setIsEditing(false);
            setInitialLoading(true);
            await loadIdentificationData();
            setInitialLoading(false);
        } catch (error) {
            console.error('Erreur complète:', error);
            toast.error('Une erreur est survenue lors de l\'envoi des données.');
        } finally {
            setIsLoading(false);
        }
    };

    const ImageDropzone = ({ onDrop, image, type, index, isDocument = false }) => {
        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop: (files) => isDocument ? onDrop(files, index) : onDrop(files, type),
            accept: { 'image/*': ['.jpeg', '.png', '.jpg'] },
            maxSize: 5 * 1024 * 1024,
            noClick: image !== null || (!isEditing && hasData)
        });

        return (
            <div className="identity-mb-4">
                <div
                    {...getRootProps()}
                    className={`identity-border-2 identity-border-dashed identity-rounded-lg identity-p-4 identity-text-center identity-cursor-pointer
                        ${isDragActive ? 'identity-border-blue-500 identity-bg-blue-50' : 'identity-border-gray-300'}
                        ${image ? 'identity-border-green-500' : ''}
                        ${(!isEditing && hasData) ? 'identity-opacity-50 identity-cursor-not-allowed' : ''}`}
                >
                    <input {...getInputProps()} disabled={!isEditing && hasData} />
                    {!image ? (
                        <div className="identity-flex identity-flex-col identity-items-center mb-5">
                            <Camera className="identity-w-12 identity-h-12 identity-text-gray-400 identity-mb-2" />
                            <p className="identity-text-gray-600">
                                {isDocument
                                    ? "Télécharger le document"
                                    : `Téléchargez votre photo ${type}`}
                            </p>
                        </div>
                    ) : (
                        <div className="identity-relative identity-w-full identity-h-48">
                            <div className="identity-w-full identity-h-full identity-border-2 identity-border-gray-300 identity-rounded-lg identity-overflow-hidden">
                                <img
                                    src={image}
                                    alt={`Uploaded ${type}`}
                                    className="identity-w-full identity-h-full identity-object-cover identity-rounded-lg"
                                />
                            </div>
                            {(isEditing || !hasData) && (
                                <button
                                    onClick={(e) => removeImage(e, type, index, isDocument)}
                                    className="identity-absolute identity-top-2 identity-right-2 identity-bg-red-500 identity-text-white identity-rounded-full identity-p-1 identity-z-10"
                                >
                                    <X className="identity-w-5 identity-h-5" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {errors[`documentImage-${index}`] && (
                    <div className="text-danger small mt-1">{errors[`documentImage-${index}`]}</div>
                )}
                {type === 'front' && errors.selfieFront && (
                    <div className="text-danger small mt-1">{errors.selfieFront}</div>
                )}
            </div>
        );
    };

    const renderDocumentTypeSelector = (doc, index) => (
        <div className="mb-3">
            <label className="form-label">
                Type de document <span className="text-danger">*</span>
            </label>
            <div className="position-relative">
                <button
                    className="form-select text-start"
                    onClick={() => handleDocumentTypePress(index)}
                    disabled={!isEditing && hasData}
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <span>{getDocumentTypeName(doc.idType)}</span>
                        <ChevronDown size={20} />
                    </div>
                </button>
                {showDocumentTypeDropdown[index] && (
                    <div className="position-absolute w-100 bg-white border rounded mt-1 z-10 shadow" style={{zIndex: 20}}>
                        {['PASSPORT', 'ID_CARD', 'DRIVERS_LICENSE', 'PERMANENT_RESIDENT_CARD'].map((type) => (
                            <button
                                key={type}
                                className="dropdown-item"
                                onClick={() => {
                                    const newDocuments = [...idDocuments];
                                    newDocuments[index].idType = type;
                                    setIdDocuments(newDocuments);
                                    setShowDocumentTypeDropdown(prev => ({ ...prev, [index]: false }));
                                }}
                            >
                                {getDocumentTypeName(type)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderCountryInput = (doc, index) => (
        <div className="mb-3">
            <label className="form-label">
                Pays de délivrance <span className="text-danger">*</span>
            </label>
            <div className="position-relative">
                <input
                    type="text"
                    className={`form-control ${errors[`country-${index}`] ? 'is-invalid' : ''}`}
                    placeholder="Pays de délivrance"
                    value={doc.countryOfIssue}
                    onChange={(e) => handleCountryInputChange(e.target.value, index)}
                    onFocus={() => setShowCountrySuggestions(prev => ({ ...prev, [index]: true }))}
                    disabled={!isEditing && hasData}
                />
                {errors[`country-${index}`] && (
                    <div className="invalid-feedback">{errors[`country-${index}`]}</div>
                )}
                {showCountrySuggestions[index] && suggestedCountries.length > 0 && (
                    <div className="position-absolute w-100 bg-white border rounded mt-1 z-10 shadow" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {suggestedCountries.map((country, idx) => (
                            <button
                                key={idx}
                                className="dropdown-item"
                                onClick={() => handleCountrySelect(country, index)}
                            >
                                {country}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderDateInput = (doc, index, dateType) => {
        const label = dateType === 'delivery' ? 'Date de délivrance' : "Date d'expiration";
        const errorKey = dateType === 'delivery' ? `deliveryDate-${index}` : `expiryDate-${index}`;
        const dateString = dateType === 'delivery' ? doc.deliveryDateString : doc.expiryDateString;
        return (
            <div className="mb-3">
                <label className="form-label">
                    {label} <span className="text-danger">*</span>
                </label>
                <input
                    type="date"
                    className={`form-control ${errors[errorKey] ? 'is-invalid' : ''}`}
                    value={dateString}
                    onChange={(e) => handleDateChange(e, index, dateType)}
                    disabled={!isEditing && hasData}
                />
                {errors[errorKey] && (
                    <div className="invalid-feedback">{errors[errorKey]}</div>
                )}
            </div>
        );
    };

    if (initialLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement des données..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <div className="verify-number-main">
                            <TopTabbar userName="Jessica" />
                            <Header />
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="verify-number-top-content">
                                        <div className="setting-header"></div>
                                    </div>
                                    <div className="home-top-content">
                                        <div className="person-details">
                                            <p className="name" style={{ fontSize: '25px' }}>Vérification d'Identité</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="verify-number-bottom" id="homepage">
                                <div className="verify-number-bottom-wrap">
                                    <div className="homepage-bottom-content">
                                        <div className="identity-container identity-mx-auto identity-px-4 identity-py-6">
                                            {isUserVerified && (
                                                <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
                                                    <CheckCircle className="me-2" />
                                                    <div>
                                                        Votre identité a déjà été vérifiée. Vous ne pouvez plus modifier ces informations.
                                                    </div>
                                                </div>
                                            )}
                                            {hasData && isEditing && !isUserVerified && (
                                                <div className="alert alert-warning mb-4">
                                                    Veuillez retirer toutes les images puis insérer les nouvelles images avant de valider la modification
                                                </div>
                                            )}
                                            {hasData && !isEditing && !isUserVerified && (
                                                <button
                                                    onClick={handleEditPress}
                                                    className="btn btn-outline-primary mb-4 d-flex align-items-center"
                                                >
                                                    <Edit2 size={18} className="me-2" />
                                                    Modifier
                                                </button>
                                            )}
                                            <div className={`identity-bg-white identity-shadow-lg identity-rounded-lg identity-p-6 ${isUserVerified ? 'identity-opacity-75' : ''}`}>
                                                <h4 className="identity-text-xl identity-font-semibold identity-mb-4">Selfie</h4>
                                                <div className="identity-grid identity-md:grid-cols-1 identity-gap-4 identity-mb-6">
                                                    <ImageDropzone
                                                        onDrop={onDropSelfie}
                                                        image={selfieImages.front}
                                                        type="front"
                                                    />
                                                </div>
                                                {idDocuments.map((doc, index) => (
                                                    <div key={index} className="identity-mb-6 mt-5 border-top pt-4">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h4 className="identity-text-xl identity-font-semibold identity-mb-0">
                                                                Document {index + 1}
                                                            </h4>
                                                            {index > 0 && (isEditing || !hasData) && (
                                                                <button
                                                                    onClick={() => handleRemoveDocument(index)}
                                                                    className="btn btn-sm btn-outline-danger"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {renderDocumentTypeSelector(doc, index)}
                                                        <ImageDropzone
                                                            onDrop={(files) => onDropDocument(files, index)}
                                                            image={doc.documentImage}
                                                            index={index}
                                                            isDocument={true}
                                                        />
                                                        {renderCountryInput(doc, index)}
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                {renderDateInput(doc, index, 'delivery')}
                                                            </div>
                                                            <div className="col-md-6">
                                                                {renderDateInput(doc, index, 'expiry')}
                                                            </div>
                                                        </div>




                                                    </div>
                                                ))}
                                                {(isEditing || !hasData) && (
                                                    <>
                                                        <button
                                                            onClick={handleAddDocument}
                                                            className="btn btn-outline-primary w-100 mb-4 d-flex align-items-center justify-content-center"
                                                        >
                                                            <Plus size={18} className="me-2" />
                                                            Ajouter un document
                                                        </button>
                                                        <div className="d-flex gap-3">
                                                            <button
                                                                onClick={hasData ? handleCancelEdit : () => window.history.back()}
                                                                className="btn btn-outline-secondary flex-grow-1"
                                                                disabled={isLoading}
                                                            >
                                                                Annuler
                                                            </button>
                                                            <button
                                                                onClick={handleSubmit}
                                                                className="btn btn-primary flex-grow-1"
                                                                disabled={isLoading}
                                                            >
                                                                {isLoading ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                        En cours...
                                                                    </>
                                                                ) : 'Soumettre'}
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default IdentificationPage;
